import APIService from 'services/APIService'
import { isNotNilOrEmpty } from 'utils/ramda'
import qs from 'qs'

const api = APIService()

// const truncateFileName = file => {
//   const maxLength = 255
//   const fileName = file.name
//   const extensionIndex = fileName.lastIndexOf('.')
//   const extension = extensionIndex > -1 ? fileName.slice(extensionIndex) : ''
//
//   // Calculate the maximum length the name can have, taking the extension into account
//   const maxNameLength = maxLength - extension.length
//
//   // Get the name part of the file (without extension)
//   const nameWithoutExtension = extensionIndex > -1 ? fileName.slice(0, extensionIndex) : fileName
//
//   // Truncate the name if necessary
//   const truncatedName = nameWithoutExtension.length > maxNameLength
//     ? nameWithoutExtension.slice(0, maxNameLength)
//     : nameWithoutExtension
//
//   // Combine the truncated name with the extension
//   const newFileName = truncatedName + extension
//
//   // Create a new File object with the truncated name
//   const newFile = new File([file], newFileName, {
//     type: file.type,
//     lastModified: file.lastModified,
//   })
//
//   return newFile
// }

export const startMultiUploadProcess = payload => {
  localStorage.removeItem('uploadFiles')
  const uploadData = {
    totalFiles: payload.totalFiles
  }

  // Add custom folder information if provided (convert to snake_case for backend)
  if (payload.customViewId) {
    uploadData.custom_view_id = payload.customViewId
  }
  if (payload.customFolderId) {
    uploadData.custom_folder_id = payload.customFolderId
  }
  // Add ZIP folder structure preservation options
  if (payload.preserveFolderStructure) {
    uploadData.preserve_folder_structure = true
  }
  if (payload.rootFolderName) {
    uploadData.root_folder_name = payload.rootFolderName
  }

  return api.post(`/building-passport/${payload.bpId}/multi-upload`, uploadData)
}

export const getMultiuploadProcessProgress = payload =>
  api.get(
    `/building-passport/${payload.bpId}/multi-upload/${payload.processId}`
  )

export const getUnitMultiuploadProcessProgress = payload =>
  api.get(
    `/building-passport/${payload.bpId}/units/${payload.unitId}/multi-upload/${payload.processId}`
  )

function addFileDetails(
  fileType,
  fd,
  expirationDate,
  floors,
  userIds,
  tagIds,
  units
) {
  fileType && fd.append('file_type_id', fileType)
  expirationDate && fd.append('expiration_date', expirationDate)

  if (isNotNilOrEmpty(floors)) {
    floors.forEach(item => {
      fd.append('floor_numbers[]', item)
    })
  }

  if (isNotNilOrEmpty(userIds)) {
    userIds.forEach(item => {
      fd.append('user_ids[]', item)
    })
  }

  if (isNotNilOrEmpty(tagIds)) {
    tagIds.forEach(item => {
      fd.append('file_tag_ids[]', item)
    })
  }

  if (isNotNilOrEmpty(units)) {
    units.forEach(item => {
      fd.append('building_unit_ids[]', item)
    })
  }
}

// Get a presigned PUT URL for direct S3 upload
export const generateUploadPresignedUrl = (bpId, processId, displayName) => {
  return api.post(
    `/building-passport/${bpId}/multi-upload/${processId}/presigned-url`,
    { display_name: displayName }
  )
}

// Upload a file directly via presigned PUT URL
// Uses XMLHttpRequest for upload progress events (bypasses axios to avoid case conversion)
export const uploadFileToS3 = (presignedUrl, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (onProgress) {
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          onProgress(percent)
        }
      })
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr)
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload network error'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'))
    })

    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    xhr.send(file)
  })
}

// Full S3 upload flow: get presigned URL → upload to S3 → notify backend with s3_key
export const uploadFileViaS3 = async (payload) => {
  const {
    file,
    displayName,
    fileType,
    floors,
    processId,
    bpId,
    units,
    expirationDate,
    userIds,
    tagIds,
    isAI,
    bpFolderId,
    customViewId,
    customFolderId,
    originalFolderPath,
    conflictResolution,
    onwardSharingAllowed
  } = payload

  // Ensure display name includes the file extension (FileModalTableRow strips it for UI display)
  const fileExt = file.name?.split('.').pop()
  const fullDisplayName = fileExt && !displayName.endsWith(`.${fileExt}`)
    ? `${displayName}.${fileExt}`
    : displayName

  // Step 1: Get presigned URL
  const presignedResp = await generateUploadPresignedUrl(bpId, processId, fullDisplayName)
  const { presignedUrl, s3Key } = presignedResp.data.data

  // Step 2: Upload file directly via presigned URL
  await uploadFileToS3(presignedUrl, file)

  // Step 3: Notify backend with s3_key (no file blob, just metadata)
  const filesInLocalStorage = JSON.parse(
    localStorage.getItem('uploadFiles') || '[]'
  )
  localStorage.setItem(
    'uploadFiles',
    JSON.stringify([...filesInLocalStorage, fullDisplayName])
  )

  const data = {
    s3_key: s3Key,
    display_name: fullDisplayName,
    upload_method: isAI ? 'ai' : 'traditional'
  }

  if (fileType) data.file_type_id = fileType
  if (expirationDate) data.expiration_date = expirationDate
  if (bpFolderId) data.bp_folder_id = bpFolderId
  if (customViewId) data.custom_view_id = customViewId
  if (customFolderId) data.custom_folder_id = customFolderId
  if (originalFolderPath) data.original_folder_path = originalFolderPath
  if (conflictResolution) data.conflict_resolution = conflictResolution
  if (onwardSharingAllowed === false) data.onward_sharing_allowed = false

  if (isNotNilOrEmpty(floors)) {
    data.floor_numbers = floors
  }
  if (isNotNilOrEmpty(userIds)) {
    data.user_ids = userIds
  }
  if (isNotNilOrEmpty(tagIds)) {
    data.file_tag_ids = tagIds
  }
  if (isNotNilOrEmpty(units)) {
    data.building_unit_ids = units
  }

  return api.post(
    `/building-passport/${bpId}/multi-upload/${processId}/file`,
    data
  )
}

export const addFileToProcess = async payload => {
  const {
    file,
    displayName,
    fileType,
    floors,
    processId,
    bpId,
    units,
    expirationDate,
    userIds,
    tagIds,
    isAI,
    bpFolderId,
    customViewId,
    customFolderId,
    originalFolderPath,
    conflictResolution,
    taskId,
    onwardSharingAllowed
  } = payload

  const fd = new FormData()
  fd.append('file', file)

  const filesInLocalStorage = JSON.parse(
    localStorage.getItem('uploadFiles') || '[]'
  )
  localStorage.setItem(
    'uploadFiles',
    JSON.stringify([...filesInLocalStorage, displayName])
  )

  fd.append('display_name', displayName)
  fd.append('upload_method', isAI ? 'ai' : 'traditional')
  addFileDetails(fileType, fd, expirationDate, floors, userIds, tagIds, units)

  // Add folder data if present (for folder upload feature)
  if (bpFolderId) {
    fd.append('bp_folder_id', bpFolderId)
  }
  // Add custom view folder data if present
  if (customViewId) {
    fd.append('custom_view_id', customViewId)
  }
  if (customFolderId) {
    fd.append('custom_folder_id', customFolderId)
  }
  if (originalFolderPath) {
    fd.append('original_folder_path', originalFolderPath)
  }
  // Add conflict resolution if present (overwrite, keep_both, or skip)
  if (conflictResolution) {
    fd.append('conflict_resolution', conflictResolution)
  }
  if (taskId) {
    fd.append('task_id', taskId)
  }
  if (onwardSharingAllowed === false) {
    fd.append('onward_sharing_allowed', '0')
  }

  return api.post(
    `/building-passport/${bpId}/multi-upload/${processId}/file`,
    fd
  )
}

export const finalizeMultiUploadProcess = payload => {
  const {
    fileId,
    fileType,
    floors,
    units,
    expirationDate,
    userIds,
    tagIds,
    bpId,
    taskId
  } = payload

  const fd = new FormData()

  addFileDetails(fileType, fd, expirationDate, floors, userIds, tagIds, units)
  taskId && fd.append('task_id', taskId)

  return api.post(
    `/building-passport/${bpId}/multi-upload/${fileId}/finalize`,
    fd
  )
}

export const startUnitMultiUploadProcess = payload =>
  api.post(
    `/building-passport/${payload.bpId}/units/${payload.unitId}/multi-upload`,
    { totalFiles: payload.totalFiles }
  )

export const addUnitFileToProcess = async payload => {
  const {
    file,
    displayName,
    fileType,
    floors,
    processId,
    bpId,
    isPrivate,
    isAI,
    expirationDate,
    taskId,
    onwardSharingAllowed
  } = payload
  const fd = new FormData()

  fd.append('display_name', displayName)
  fd.append('upload_method', isAI ? 'ai' : 'traditional')
  isPrivate && fd.append('is_private', '1')
  expirationDate && fd.append('expiration_date', expirationDate)
  isNotNilOrEmpty(fileType) && fd.append('file_type_id', fileType)
  taskId && fd.append('task_id', taskId)
  onwardSharingAllowed === false && fd.append('onward_sharing_allowed', '0')
  fd.append('file', file)

  if (isNotNilOrEmpty(floors)) {
    floors.forEach(item => {
      fd.append('floor_numbers[]', item)
    })
  }
  return api.post(
    `/building-passport/${bpId}/units/${payload.unitId}/multi-upload/${processId}/file`,
    fd
  )
}

export const getFilesCounts = payload => {
  const { id, query } = payload
  const queryString = query ? qs.stringify(query, { addQueryPrefix: true }) : ''
  return api.get(`/building-passport/${id}/files/counts${queryString}`)
}

export const getFileProcessingStatus = bpId =>
  api.get(`/building-passport/${bpId}/files/processing-status`)

export const getFilesStatistics = payload => {
  const { id } = payload
  return api.get(`/building-passport/${id}/files/statistics`)
}

export const getFilesListForBp = (payload, signal) => {
  const { id, query } = payload
  let queryValues = { page: 1, limit: 3000 }

  let queryString = ''

  if (query) {
    queryValues = { ...queryValues, ...query }
  }

  queryString = qs.stringify(queryValues, { addQueryPrefix: true })
  return api.get(`/building-passport/${id}/files${queryString}`, signal ? { signal } : {})
}

export const getRecentFiles = payload => {
  const { id } = payload
  return api.get(`/building-passport/${id}/files/recent`)
}

export const getCustomViewFiles = (payload, signal) => {
  const { bpId, customViewId, query } = payload
  let queryValues = { page: 1, limit: 3000 }

  let queryString = ''

  if (query) {
    queryValues = { ...queryValues, ...query }
  }

  queryString = qs.stringify(queryValues, { addQueryPrefix: true })
  return api.get(`/building-passport/${bpId}/custom-views/${customViewId}/files${queryString}`, signal ? { signal } : {})
}

export const getCustomFolderFiles = (payload, signal) => {
  const { bpId, customViewId, customFolderId, query } = payload
  let queryValues = { page: 1, limit: 3000 }

  let queryString = ''

  if (query) {
    queryValues = { ...queryValues, ...query }
  }

  queryString = qs.stringify(queryValues, { addQueryPrefix: true })
  return api.get(`/building-passport/${bpId}/custom-views/${customViewId}/folders/${customFolderId}/files${queryString}`, signal ? { signal } : {})
}

export const searchLocalFiles = (payload, signal) => {
  const { bpId, query, exactMatch, uploaderIds, fileTypeIds, fileCategoryIds, tagIds, searchMode, unlimited, customViewId, viewableByUserId } = payload

  const params = {
    query: query || ''
  }

  // Add search mode parameter (default to 'name' if not specified)
  if (searchMode) {
    params.search_mode = searchMode
  }

  // Add boolean parameters
  if (exactMatch) {
    params.exact_match = true
  }

  if (unlimited) {
    params.unlimited = true
  }

  // Add array parameters
  if (uploaderIds?.length > 0) {
    params.uploader_ids = uploaderIds
  }

  if (fileTypeIds?.length > 0) {
    params.file_type_ids = fileTypeIds
  }

  if (fileCategoryIds?.length > 0) {
    params.file_category_ids = fileCategoryIds
  }

  if (tagIds?.length > 0) {
    params.file_tag_ids = tagIds
  }

  if (customViewId) {
    params.custom_view_id = customViewId
  }

  if (viewableByUserId) {
    params.viewable_by_user_id = viewableByUserId
  }

  const queryString = qs.stringify(params, {
    arrayFormat: 'brackets',
    skipNulls: true
  })

  return api.get(`/building-passport/${bpId}/search-file?${queryString}`, signal ? { signal } : {})
}

export const getTagsListForBp = payload => {
  return api.get(`/building-passport/${payload.bpId}/file-tags`)
}

export const createFileTag = payload => {
  return api.post(`/building-passport/${payload.bpId}/file-tags`, {
    name: payload.name
  })
}

export const removeFileTag = payload => {
  return api.delete(
    `/building-passport/${payload.bpId}/file-tags/tag/${payload.id}`
  )
}

export const attachFileTagToFile = payload => {
  return api.post(
    `/building-passport/${payload.bpId}/file-tags/${payload.id}/attach`,
    { fileId: payload.fileId }
  )
}

export const syncFileTags = payload => {
  return api.post(`/building-passport/${payload.bpId}/file-tags/sync`, {
    fileId: payload.fileId,
    tagIds: payload.tagIds,
    newTags: payload.newTags
  })
}

export const syncFileVersionTags = payload => {
  return api.post(
    `/building-passport/${payload.bpId}/file-tags/file-versions/sync`,
    {
      fileVersionId: payload.fileVersionId,
      tagIds: payload.tagIds,
      newTags: payload.newTags
    }
  )
}

export const detachFileTagFromFile = payload => {
  return api.delete(
    `/building-passport/${payload.bpId}/file-tags/${payload.id}`
  )
}

export const markFileAsViewed = payload => {
  return api.post(
    `/building-passport/files/${payload.fileId}/${payload.fileVersionId}/log-access`,
    { type: payload.type }
  )
}

export const getFileViewedLogs = payload => {
  return api.get(`/building-passport/files/${payload.fileId}/access-logs`)
}

export const getAccessHistory = (fileId, cursor) => {
  return api.get(`/building-passport/files/${fileId}/access-history`, { params: cursor ? { cursor } : {} })
}

export const bulkShareFiles = payload => {
  return api.post('/building-passport/files/bulk-share', payload)
}

export const fetchExternalSharedFiles = fileShareId => {
  return api.get(`/external-access/${fileShareId}`)
}

export const logExternalDownload = ({ fileId, shareToken, email }) => {
  return api.post(`/external-access/${fileId}/${shareToken}/download-log`, {
    email
  })
}

export const logExternalPreview = ({ fileId, shareToken, email }) => {
  return api.post(`/external-access/${fileId}/${shareToken}/preview-log`, {
    email
  })
}

export const bulkUnsharedFiles = payload => {
  return api.post('/building-passport/files/bulk-unshare', payload)
}

export const fetchInternalSharedUsers = payload => {
  return api.post('/building-passport/files/internal-shares', {
    fileIds: payload
  })
}
export const fetchExternalSharedUsers = fileIds => {
  return api.post('/building-passport/files/shared-users', {
    file_ids: fileIds
  })
}

export const fetchSharedUsersForFiles = (fileIds) => {
  return api.post('/building-passport/files/shared-users', {
    file_ids: fileIds
  })
}
export const checkUserByEmail = (email, bpId) =>
  api.post(`/building-passport/${bpId}/check-users-by-emails`, {
    emails: [email],
    bp_id: bpId
  })
export const bulkEditFiles = payload => {
  return api.post('/building-passport/files/bulk-edit', payload)
}

export const bulkDeleteFiles = fileIds =>
  api.post('/building-passport/files/bulk-delete', { fileIds })

export const bulkDownloadFiles = (bpId, fileIds) =>
  api.post(`/building-passport/${bpId}/files/bulk-download`, { fileIds }, { responseType: 'blob' })

export const toggleUserPermissionsToFiles = (bpId, userId, files) =>
  api.patch(`/building-passport/${bpId}/files-access`, { userId, files })

export const getGuruInsight = payload => {
  const { bpId, fileId } = payload
  return api.get(`/building-passport/${bpId}/files/${fileId}/guru-insight`)
}

export const separateFileVersion = fileId => api.post(`/building-passport/files/${fileId}/separate-version`)

export const rejoinFileVersion = fileId => api.post(`/building-passport/files/${fileId}/rejoin-version`)

export const reclassifyFile = payload => {
  const { fileId, fileTypeId } = payload
  return api.post(`/building-passport/files/${fileId}/reclassify`, {
    file_type_id: fileTypeId
  })
}

export const getRecommendedFileTypes = payload => {
  const { fileId } = payload
  return api.get(`/building-passport/files/${fileId}/type-recommendations`)
}

export const extendSession = () => {
  return api.post('/session/extend')
}

// Duplicates management
export const getDuplicates = (bpId, { page, perPage, type } = {}) => {
  const params = {}
  if (page) params.page = page
  if (perPage) params.per_page = perPage
  if (type) params.type = type
  return api.get(`/building-passport/${bpId}/files/duplicates`, { params })
}

export const bulkDeleteDuplicates = (bpId, fileIds, keepFileId) => {
  const payload = { fileIds: fileIds }
  if (keepFileId) {
    payload.keepFileId = keepFileId
  }
  return api.delete(`/building-passport/${bpId}/files/duplicates/bulk-delete`, payload)
}

export const markFilesForDeletion = (bpId, fileIds, notes) => {
  const payload = { fileIds: fileIds }
  if (notes) {
    payload.notes = notes
  }
  return api.post(`/building-passport/${bpId}/files/duplicates/mark-for-deletion`, payload)
}

export const unmarkFilesFromDeletion = (bpId, fileIds) => {
  return api.post(`/building-passport/${bpId}/files/duplicates/unmark-for-deletion`, {
    fileIds: fileIds
  })
}

export const getMarkedFilesExport = (bpId, format = 'json') => {
  return api.get(`/building-passport/${bpId}/files/duplicates/export-marked?format=${format}`)
}

export const downloadMarkedFilesCsv = (bpId) => {
  return api.get(`/building-passport/${bpId}/files/duplicates/export-marked?format=csv`, {
    responseType: 'blob'
  })
}

// File analysis tool
export const getFileAnalysisSummary = (bpId) =>
  api.get(`/building-passport/${bpId}/files/analysis/summary`)

export const getCorruptedFiles = (bpId, { page = 1, perPage = 25, search } = {}) =>
  api.get(`/building-passport/${bpId}/files/analysis/corrupted`, { params: { page, per_page: perPage, search } })

export const getRejectedUploads = (bpId, { page = 1, perPage = 25, batch } = {}) =>
  api.get(`/building-passport/${bpId}/files/analysis/rejected`, { params: { page, per_page: perPage, batch } })

export const getUploadBatches = (bpId, { page = 1, perPage = 25 } = {}) =>
  api.get(`/building-passport/${bpId}/files/analysis/batches`, { params: { page, per_page: perPage } })

export const getUploadBatchFiles = (bpId, { date, page = 1, perPage = 50 } = {}) =>
  api.get(`/building-passport/${bpId}/files/analysis/batch-files`, { params: { date, page, per_page: perPage } })

export const bulkDeleteBatchFiles = (bpId, { fileIds, date } = {}) =>
  api.post(`/building-passport/${bpId}/files/analysis/batch-files/delete`, { file_ids: fileIds, date })

export const getWrongBuildingFiles = (bpId, { page = 1, perPage = 25 } = {}) =>
  api.get(`/building-passport/${bpId}/files/analysis/wrong-building`, { params: { page, per_page: perPage } })

export const retryFileOcr = (bpId, fileId) =>
  api.post(`/building-passport/${bpId}/files/${fileId}/retry-ocr`, {})

export const resolveWrongBuildingFlag = (bpId, flagId) =>
  api.post(`/building-passport/${bpId}/files/analysis/wrong-building/${flagId}/resolve`, {})

export const deleteBpFile = (bpId, fileId) =>
  api.delete(`/building-passport/${bpId}/files/${fileId}`)

export const restoreBpFile = (bpId, fileId) =>
  api.post(`/building-passport/${bpId}/files/${fileId}/restore`)

export const getReviewFiles = (bpId, { page = 1, perPage = 25, search, source, fileTypeId, fileCategoryId, flagged } = {}) => {
  const params = { page, per_page: perPage }
  if (flagged) params.flagged = 1
  if (search) params.search = search
  if (source) params.source = source
  if (fileTypeId) params.file_type_id = fileTypeId
  if (fileCategoryId) params.file_category_id = fileCategoryId
  return api.get(`/building-passport/${bpId}/files/review-list`, { params })
}

export const setFileReviewAction = (bpId, fileId, action, notes) =>
  api.post(`/building-passport/${bpId}/files/${fileId}/review-action`, notes ? { action, notes } : { action })

export const getFileReviewActions = (bpId, { page = 1, perPage = 25, direction } = {}) =>
  api.get(`/building-passport/${bpId}/files/review-actions`, { params: { page, per_page: perPage, ...(direction ? { direction } : {}) } })

export const getRecommendedRemedials = (bpId, { page = 1, perPage = 25, status, severity, search, sort, dir } = {}) =>
  api.get(`/building-passport/${bpId}/recommended-remedials`, { params: {
    page,
    per_page: perPage,
    ...(status ? { status } : {}),
    ...(severity ? { severity } : {}),
    ...(search ? { search } : {}),
    ...(sort ? { sort, dir: dir || 'desc' } : {})
  } })

export const getFileUploaders = bpId => {
  return api.get(`/building-passport/${bpId}/files/uploaders`)
}

// Fetch file URLs on demand (for preview/download)
// This is used because we don't include URLs in the file list for performance
export const getFileUrls = (bpId, fileId) => {
  return api.get(`/building-passport/${bpId}/files/${fileId}/urls`)
}

// Fetch thumbnail URLs for multiple files (batch endpoint for lazy loading)
export const getFileThumbnails = (bpId, fileIds) => {
  return api.post(`/building-passport/${bpId}/files/thumbnails`, {
    file_ids: fileIds
  })
}

export const getUnitFilingProposals = (bpId) =>
  api.get(`/building-passport/${bpId}/unit-filing/proposals`)

export const applyUnitFiling = (bpId, pairs) =>
  api.post(`/building-passport/${bpId}/unit-filing/apply`, { pairs })
